// src/RefundPolicy.jsx
import React from 'react';
import NavigationBar from './NavigationBar';
import './PolicyPages.css';

const RefundPolicy = () => {
  return (
    <>
      <NavigationBar />
      <div className="policy-page-container">
        <div className="policy-content">
          <h1>Refund & Cancellation Policy</h1>
          <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2>1. Subscription Overview</h2>
            <p>
              TickrTrends offers a monthly subscription service at $4.99 per month. By subscribing, you agree to:
            </p>
            <ul>
              <li>Automatic recurring monthly billing until cancellation</li>
              <li>Immediate access to all premium features upon payment</li>
              <li>The terms outlined in this Refund Policy</li>
            </ul>
            <p>
              <strong>Important:</strong> TickrTrends is a data aggregation and analysis tool. You are purchasing 
              access to software and information, not investment advice or guaranteed returns.
            </p>
          </section>

          <section>
            <h2>2. Cancellation Policy</h2>
            
            <h3>2.1 How to Cancel</h3>
            <p>You may cancel your subscription at any time through:</p>
            <ul>
              <li>Your account settings page (Settings → Subscription Management)</li>
              <li>Email request to ryan@tickrtrends.com</li>
            </ul>
            <p>
              Cancellation requests via email will be processed within 1-2 business days.
            </p>

            <h3>2.2 When Cancellation Takes Effect</h3>
            <p>
              Upon cancellation:
            </p>
            <ul>
              <li>You will retain access through the end of your current billing period</li>
              <li>Your subscription will NOT automatically renew</li>
              <li>You will NOT be charged for subsequent months</li>
              <li>Access to premium features will cease at the end of the paid period</li>
            </ul>

            <h3>2.3 No Partial Month Refunds</h3>
            <p>
              <strong>Cancellations do NOT result in prorated refunds.</strong> When you cancel, you continue 
              to have full access until the end of your current billing period. No refunds are issued for 
              unused time in the current month.
            </p>
          </section>

          <section>
            <h2>3. Refund Policy</h2>

            <h3>3.1 7-Day Money-Back Guarantee (First-Time Subscribers Only)</h3>
            <p>
              We offer a <strong>7-day money-back guarantee for first-time subscribers only.</strong>
            </p>
            <p>
              <strong>Eligibility Requirements:</strong>
            </p>
            <ul>
              <li>You must be a first-time subscriber (never previously subscribed to TickrTrends)</li>
              <li>You must request a refund within 7 calendar days of your initial subscription date</li>
              <li>You must contact ryan@tickrtrends.com with "Refund Request" in the subject line</li>
            </ul>
            <p>
              <strong>What You'll Receive:</strong>
            </p>
            <ul>
              <li>Full refund of your first month's subscription fee ($4.99)</li>
              <li>Refund processed to your original payment method within 5-10 business days</li>
              <li>Immediate termination of account access</li>
            </ul>

            <h3>3.2 No Refunds After 7 Days</h3>
            <p>
              <strong>After the 7-day period, all fees are non-refundable.</strong> This policy applies to:
            </p>
            <ul>
              <li>Renewal payments (2nd month and beyond)</li>
              <li>Reactivated subscriptions</li>
              <li>Any subscription beyond the initial 7-day guarantee period</li>
            </ul>
            <p>
              <strong>Reason:</strong> TickrTrends provides immediate access to data, analysis, and premium features 
              upon subscription. Once accessed, the service has been delivered. We strongly recommend canceling 
              before your next billing date if you do not wish to continue.
            </p>

            <h3>3.3 Exceptions</h3>
            <p>
              Refunds beyond the 7-day guarantee may be granted at our sole discretion in exceptional circumstances:
            </p>
            <ul>
              <li><strong>Technical Issues:</strong> Prolonged service outages preventing access (verified by our logs)</li>
              <li><strong>Billing Errors:</strong> Duplicate charges or incorrect amounts</li>
              <li><strong>Fraudulent Charges:</strong> Unauthorized use of your payment method (requires documentation)</li>
              <li><strong>Force Majeure:</strong> Events beyond our control preventing service delivery</li>
            </ul>
            <p>
              Exception requests must be submitted to ryan@tickrtrends.com with supporting documentation. 
              We will investigate and respond within 5 business days. Our decision is final.
            </p>

            <h3>3.4 What Does NOT Qualify for Refunds</h3>
            <p>
              <strong>Refunds will NOT be issued for:</strong>
            </p>
            <ul>
              <li>Change of mind after the 7-day guarantee period</li>
              <li>Dissatisfaction with market performance or investment results</li>
              <li>Failure to cancel before renewal date</li>
              <li>Misunderstanding of the Service (please review our Terms of Service)</li>
              <li>Personal financial circumstances or inability to afford subscription</li>
              <li>Account termination due to Terms of Service violations</li>
              <li>Forgetting about an active subscription</li>
            </ul>
          </section>

          <section>
            <h2>4. Billing and Payment Issues</h2>
            
            <h3>4.1 Failed Payments</h3>
            <p>
              If your payment method is declined:
            </p>
            <ul>
              <li>Your subscription will be immediately suspended</li>
              <li>You will receive an email notification at your registered address</li>
              <li>We will retry payment up to 3 times over 7 days</li>
              <li>If payment fails after 3 attempts, your account will be canceled</li>
              <li>You are responsible for updating your payment information</li>
            </ul>

            <h3>4.2 Duplicate or Incorrect Charges</h3>
            <p>
              If you believe you were charged incorrectly:
            </p>
            <ul>
              <li>Contact ryan@tickrtrends.com within 30 days of the charge</li>
              <li>Provide your account email and transaction details</li>
              <li>We will investigate within 3-5 business days</li>
              <li>Verified errors will be refunded within 10 business days</li>
            </ul>

            <h3>4.3 Chargebacks</h3>
            <p>
              <strong>Important:</strong> If you initiate a chargeback without first contacting us:
            </p>
            <ul>
              <li>Your account will be immediately terminated</li>
              <li>You may be permanently banned from future use of the Service</li>
              <li>We reserve the right to pursue collection of fees owed</li>
            </ul>
            <p>
              Please contact us BEFORE initiating a chargeback. We are committed to resolving billing 
              disputes fairly and promptly.
            </p>
          </section>

          <section>
            <h2>5. Refund Processing</h2>
            <p>
              <strong>For approved refunds:</strong>
            </p>
            <ul>
              <li>Processing time: 5-10 business days from approval</li>
              <li>Refunds issued to original payment method only</li>
              <li>Bank processing may take additional 3-5 business days</li>
              <li>You will receive email confirmation when refund is processed</li>
              <li>Account access terminates immediately upon refund approval</li>
            </ul>
            <p>
              <strong>Note:</strong> We cannot expedite refund processing beyond our standard timeframe. 
              Refund timing depends on your financial institution's policies.
            </p>
          </section>

          <section>
            <h2>6. Price Changes</h2>
            <p>
              We reserve the right to modify subscription pricing at any time. For existing subscribers:
            </p>
            <ul>
              <li>You will receive 30 days advance notice via email</li>
              <li>New pricing applies to your next billing cycle after notice period</li>
              <li>You may cancel before the price change to avoid the new rate</li>
              <li>Continued subscription after notice period constitutes acceptance of new pricing</li>
            </ul>
            <p>
              <strong>Grandfathered Pricing:</strong> We do not guarantee grandfathered pricing. All subscribers 
              may be subject to price changes with proper notice.
            </p>
          </section>

          <section>
            <h2>7. Account Termination by TickrTrends</h2>
            <p>
              We reserve the right to terminate accounts for:
            </p>
            <ul>
              <li>Violation of our Terms of Service</li>
              <li>Fraudulent activity or payment abuse</li>
              <li>Sharing account credentials</li>
              <li>Automated data scraping or extraction</li>
              <li>Abusive behavior toward staff or other users</li>
              <li>Any reason at our sole discretion</li>
            </ul>
            <p>
              <strong>No refunds will be issued for accounts terminated due to violations.</strong>
            </p>
          </section>

          <section>
            <h2>8. Service Discontinuation</h2>
            <p>
              If TickrTrends permanently discontinues the Service:
            </p>
            <ul>
              <li>We will provide 30 days advance notice</li>
              <li>Subscribers will receive a prorated refund for unused time</li>
              <li>No refunds for past months already accessed</li>
            </ul>
          </section>

          <section>
            <h2>9. Important Reminders</h2>
            <div style={{ 
              background: 'rgba(245, 158, 11, 0.1)', 
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '20px'
            }}>
              <h3 style={{ color: '#f59e0b', marginTop: 0 }}>⚠️ Before You Subscribe</h3>
              <ul>
                <li>TickrTrends is a <strong>data tool</strong>, not an investment advisor</li>
                <li>We do NOT guarantee profits or investment success</li>
                <li>You have 7 days to request a refund if you're a first-time subscriber</li>
                <li>After 7 days, all fees are non-refundable</li>
                <li>Cancel before your renewal date to avoid charges</li>
                <li>Review our Terms of Service before subscribing</li>
              </ul>
            </div>
          </section>

          <section>
            <h2>10. Contact Information</h2>
            <p>
              Questions about refunds, cancellations, or billing?
              <br />
              <strong>Email:</strong> ryan@tickrtrends.com
              <br />
              <strong>Subject Line:</strong> Include "Billing Question" or "Refund Request"
              <br />
              <strong>Response Time:</strong> 1-2 business days (5 business days for refund investigations)
            </p>
            <p>
              Please include your account email address and detailed description of your issue.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default RefundPolicy;